import { json, request, send } from '@/lib/api'

import { decodeDataGridResponse } from '../lib/decode'
import type {
  AnyDataGridContract,
  DataGridParameterValue,
  DataGridQuery,
  DataGridResponse,
} from '../lib/types'

type DataGridParameterValues = Readonly<Record<string, DataGridParameterValue>>

export type DataGridRequest<Contract extends AnyDataGridContract> = {
  query: DataGridQuery<Contract>
  signal: AbortSignal
  /** The grid's declared parameters, which the endpoint reads from the query string under their ids. */
  parameters?: DataGridParameterValues
}

export type DataGridExportRequest<Contract extends AnyDataGridContract> = {
  query: Omit<DataGridQuery<Contract>, 'pagination' | 'facets'>
  signal: AbortSignal
  parameters?: DataGridParameterValues
}

export type DataGridExport = {
  blob: Blob
  filename: string
}

export type DataGridClient<Contract extends AnyDataGridContract> = {
  query: (input: DataGridRequest<Contract>) => Promise<DataGridResponse<Contract>>
  export: (input: DataGridExportRequest<Contract>) => Promise<DataGridExport>
}

/** Creates the standard query and CSV calls from the base endpoint mapped by the backend grid primitive. */
export function createDataGridClient<Contract extends AnyDataGridContract>({
  contract,
  endpoint,
}: {
  contract: Contract
  endpoint: string
}): DataGridClient<Contract> {
  const baseEndpoint = normalizeEndpoint(endpoint)

  return {
    query: ({ query, signal, parameters }) => request(
      operationEndpoint(appendQueryParams(baseEndpoint, parameters), ':query'),
      decodeDataGridResponse(contract),
      { ...json('POST', query), signal },
    ),
    export: async ({ query, signal, parameters }) => {
      const response = await send(operationEndpoint(appendQueryParams(baseEndpoint, parameters), ':export'), {
        ...json('POST', query),
        headers: {
          'Accept': 'text/csv',
          'Content-Type': 'application/json',
        },
        signal,
      })
      return {
        blob: await response.blob(),
        filename: responseFilename(response) ?? `${contract.id}.csv`,
      }
    },
  }
}

function responseFilename(response: Response) {
  const disposition = response.headers.get('Content-Disposition')
  if (!disposition) return undefined
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1]?.trim()
  if (encoded) {
    try {
      return decodeURIComponent(encoded)
    } catch {
      return undefined
    }
  }
  return disposition.match(/filename="([^"]+)"/i)?.[1]
    ?? disposition.match(/filename=([^;]+)/i)?.[1]?.trim()
}

function normalizeEndpoint(endpoint: string) {
  const boundary = endpoint.search(/[?#]/)
  const path = boundary === -1 ? endpoint : endpoint.slice(0, boundary)
  const suffix = boundary === -1 ? '' : endpoint.slice(boundary)
  return `${path.length > 1 ? path.replace(/\/+$/, '') : path}${suffix}`
}

function appendQueryParams(endpoint: string, parameters?: DataGridParameterValues) {
  const entries = Object.entries(parameters ?? {})
    .map(([key, value]) => [key, String(value)] as const)
    .sort(([left], [right]) => left.localeCompare(right))
  if (entries.length === 0) return endpoint

  const hashBoundary = endpoint.indexOf('#')
  const address = hashBoundary === -1 ? endpoint : endpoint.slice(0, hashBoundary)
  const hash = hashBoundary === -1 ? '' : endpoint.slice(hashBoundary)
  const queryBoundary = address.indexOf('?')
  const path = queryBoundary === -1 ? address : address.slice(0, queryBoundary)
  const query = new URLSearchParams(
    queryBoundary === -1 ? '' : address.slice(queryBoundary + 1),
  )
  for (const [key, value] of entries) query.set(key, value)
  return `${path}?${query}${hash}`
}

function operationEndpoint(endpoint: string, operation: ':query' | ':export') {
  const boundary = endpoint.search(/[?#]/)
  return boundary === -1
    ? `${endpoint}${operation}`
    : `${endpoint.slice(0, boundary)}${operation}${endpoint.slice(boundary)}`
}
